import React from 'react';
import _isObject from 'lodash/isObject';
import { PageHeader } from 'ui/pageHeader';
import { Skeleton } from 'ui/skeleton';
import noop from 'utils/noop';
import CatalogFormCSV, { CatalogFormJSON } from 'modules/catalogForm';
import CreateCollectionModal from 'modules/catalog/createCollectionModal';
import { DeleteCatalogModal } from './DeleteCatalogModal';
import { CatalogTableButtonRow } from './CatalogTableButtonRow';
import SubHeader from './SubHeader';
import { StyledCoreTable } from './CatalogTable.styled';
import { UPDATE_CATALOG_ITEM_METHOD } from '../constants';
import { CatalogTableToolbarContent } from './CatalogTableToolbarContent';

// type Props = {
//   /** Whether user has RBAC permission to manage catalogs */
//   canManageCatalogs: Boolean,
//   /** Catalog metadata to display in subheader */
//   catalogMetadataItems: Array<Object>,
//   /** Callback when table page size changes. */
//   changeTablePageSize: (number, number) => {},
//   /** Defined fields as object where key is field name and value is field type */
//   definedFields: Object,
//   /** Table header */
//   headerRow: Array<Object>,
//   /** Table data */
//   rowData: Array<*>,
//   /** Function to fetch field types */
//   fetchCatalogFieldTypesRequest: (string) => void,
//   /** Function to fetch paginated catalog items */
//   fetchPaginatedCatalogItems: (*, Object) => void,
//   /** Table filters */
//   filters: Object,
//   /** Function to fire Segment tracking call */
//   fireSegmentTrackCall: () => void,
//   /** Boolean for feature flag CatalogCollections */
//   isCatalogCollectionsFlagOn: boolean,
//   /** Boolean for displaying as Table (spreadsheet) or JSON */
//   isDisplayTable: boolean,
//   /** Boolean for loading */
//   isLoading: boolean,
//   /** Boolean for form is submitting */
//   isSubmitting: boolean,
//   /** Function to delete item in catalog */
//   removeCatalogItem: (string, ?string) => void,
//   /** Callback function when table if refreshed. */
//   refreshTable: () => void,
//   /** Catalog name - if set viewing specific catalog */
//   tableName: string,
//   /** Number of catalogs or items in catalog */
//   total: number,
//   /** Function to update CatalogDisplaySetting */
//   updateCatalogDisplaySetting: (boolean) => void,
//   /** Array of undefined field names */
//   undefinedFields: Array<string>,
//   /** Boolean for the user's ability to manage catalogs */
//   canManageCatalogs: boolean,
// };

// type State = {
//   updateCatalogIsOpen: boolean,
//   deleteCatalogModalIsOpen: boolean,
//   deleteName: ?string,
// };

export class CatalogTable extends React.Component {
  static defaultProps = {
    headerRow: [],
    rowData: [],
    filters: { page: 1, pageSize: 10 },
    undefinedFields: [],
    definedFields: {},
    changeTablePageSize: noop,
    createNewCatalogRequest: noop,
    fetchPaginatedCatalogItems: noop,
    removeCatalogItem: noop,
    refreshTable: noop,
    updateCatalogDisplaySetting: noop,
    fetchCatalogFieldTypesRequest: noop,
    fireSegmentTrackCall: noop,
  };

  constructor(props) {
    super(props);
    this.state = {
      updateCatalogIsOpen: false,
      deleteCatalogModalIsOpen: false,
      deleteName: '',
      updateMethod: '',
      selectedTableValue: null,
      selectedTableKey: null,
      createCollectionModalIsOpen: false,
    };
  }

  openCreateCollectionModal = () => {
    this.setState({ createCollectionModalIsOpen: true });
  };

  closeCreateCollectionModal = () => {
    this.setState({ createCollectionModalIsOpen: false });
  };

  closeUpdateItemModal = () => {
    const { filters, fetchPaginatedCatalogItems, tableName } = this.props;
    this.setState({
      updateCatalogIsOpen: false,
      updateMethod: '',
      selectedTableValue: null,
      selectedTableKey: null,
    });
    fetchPaginatedCatalogItems(tableName, filters);
  };

  closeDeleteModal = () => {
    this.setState({
      deleteCatalogModalIsOpen: false,
      deleteName: '',
    });
  };

  openDeleteModal = (e, { key }) => {
    this.setState({
      deleteCatalogModalIsOpen: true,
      deleteName: key,
    });
  };

  openEditItemJSONModal = (e, { key, value }) => {
    const { fetchCatalogFieldTypesRequest, tableName } = this.props;
    const selectedTableValue = _isObject(value) ? JSON.stringify(value) : value;
    fetchCatalogFieldTypesRequest(tableName);
    this.setState({
      updateCatalogIsOpen: true,
      updateMethod: UPDATE_CATALOG_ITEM_METHOD.JSON,
      selectedTableValue,
      selectedTableKey: key,
    });
  };

  deleteOnClickHandler = () => {
    const { tableName } = this.props;
    const { deleteName } = this.state;
    this.props.removeCatalogItem(tableName, deleteName);
    this.setState({
      deleteCatalogModalIsOpen: false,
      deleteName: '',
    });
  };

  updateColsWithActions = (cols) => {
    const firstCol = {
      ...cols.slice(0, 1)[0],
      actionCell: {
        header: '',
        onClickDelete: this.openDeleteModal,
        onClickEdit: this.openEditItemJSONModal,
      },
    };
    return [firstCol, ...cols.slice(1)];
  };

  handleChangeTableFilters = (e, pageProps) => {
    const { fetchPaginatedCatalogItems, filters, tableName } = this.props;
    const newFilters = { ...filters, ...(pageProps || '') };
    fetchPaginatedCatalogItems(tableName, newFilters);
  };

  handleChangePageSize = (e, pageProps) => {
    const { changeTablePageSize, filters } = this.props;
    this.handleChangeTableFilters(e, pageProps);
    changeTablePageSize(filters.pageSize, pageProps.pageSize);
  };

  handleRefreshTable = (e, pageProps) => {
    this.props.refreshTable();
    this.handleChangeTableFilters(e, pageProps);
  };

  handleJsonOrTableOnClick = (e, { isTableView }) => {
    const { localStorage } = window;
    const { updateCatalogDisplaySetting } = this.props;
    if (localStorage) {
      localStorage.setItem('catalogDisplayAsTable', isTableView);
    }
    updateCatalogDisplaySetting(isTableView);
  };

  handleChangeUpdateMethodCatalogItems = (value) => {
    if (value in UPDATE_CATALOG_ITEM_METHOD) {
      const { fetchCatalogFieldTypesRequest, fireSegmentTrackCall, tableName } = this.props;
      fireSegmentTrackCall('Catalog Items Update Started', {
        catalog_name: tableName,
        creation_method: 'ui',
        ui_method: value.toLowerCase(),
      });
      fetchCatalogFieldTypesRequest(tableName);
      this.setState({ updateMethod: value, updateCatalogIsOpen: true });
    }
  };

  render() {
    const {
      canManageCatalogs,
      catalogMetadataItems,
      isCatalogCollectionsFlagOn,
      isLoading,
      isSubmitting,
      isDisplayTable,
      filters,
      tableName,
      headerRow,
      rowData,
      total,
      definedFields,
      undefinedFields,
      fireSegmentTrackCall,
    } = this.props;
    const {
      updateCatalogIsOpen,
      deleteCatalogModalIsOpen,
      deleteName,
      updateMethod,
      selectedTableValue,
      selectedTableKey,
      createCollectionModalIsOpen,
    } = this.state;

    const buttonRowContent = (
      <CatalogTableButtonRow
        canManageCatalogs={canManageCatalogs}
        fireSegmentTrackCall={fireSegmentTrackCall}
        isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
        openCreateCollectionModal={this.openCreateCollectionModal}
        tableName={tableName}
        definedFields={definedFields}
        undefinedFields={undefinedFields}
      />
    );

    return (
      <div>
        <CatalogFormJSON
          updateCatalogIsOpen={
            updateMethod === UPDATE_CATALOG_ITEM_METHOD.JSON && updateCatalogIsOpen
          }
          closeUpdateItemModal={this.closeUpdateItemModal}
          tableName={tableName}
          tableKey={selectedTableKey || ''}
          tableValue={selectedTableValue || '{}'}
          isSubmitting={isSubmitting}
        />
        <CatalogFormCSV
          updateCatalogIsOpen={
            updateMethod === UPDATE_CATALOG_ITEM_METHOD.CSV && updateCatalogIsOpen
          }
          closeUpdateItemModal={this.closeUpdateItemModal}
          tableName={tableName}
          isSubmitting={isSubmitting}
          updateMethod={updateMethod}
        />
        <DeleteCatalogModal
          onDelete={this.deleteOnClickHandler}
          isOpen={deleteCatalogModalIsOpen}
          closeModal={this.closeDeleteModal}
          name={deleteName}
        />
        {isCatalogCollectionsFlagOn && (
          <CreateCollectionModal
            catalog={tableName}
            isOpen={createCollectionModalIsOpen}
            closeModal={this.closeCreateCollectionModal}
          />
        )}
        <PageHeader
          breadcrumb={{
            content: 'Catalogs',
            href: '/catalogs',
          }}
          pageSubSection={<SubHeader items={catalogMetadataItems} />}
          pageActions={buttonRowContent}
        >
          {tableName}
        </PageHeader>
        {isLoading ? (
          <Skeleton height={600} />
        ) : (
          <StyledCoreTable
            columns={canManageCatalogs ? this.updateColsWithActions(headerRow) : headerRow}
            rows={rowData}
            hidePagination={false}
            dockLeftColumn={isDisplayTable}
            expandColumn={!isDisplayTable}
            onChangePage={this.handleChangeTableFilters}
            onChangePageSize={this.handleChangePageSize}
            onChangeSort={this.handleChangeTableFilters}
            sorting={filters.sorting}
            orderBy={filters.orderBy}
            page={filters.page}
            pageSize={filters.pageSize}
            total={total || (rowData && rowData.length) || 0}
            emptyStateHeader={'No data in catalog'}
            emptyStateText={'Try adding items to your catalog'}
            pageSizeInToolbar={true}
            toolbarContent={
              <CatalogTableToolbarContent
                canManageCatalogs={canManageCatalogs}
                handleChangeUpdateMethodCatalogItems={this.handleChangeUpdateMethodCatalogItems}
                handleJsonOrTableOnClick={this.handleJsonOrTableOnClick}
                handleRefreshTable={this.handleRefreshTable}
                isDisplayTable={isDisplayTable}
              />
            }
            showToggleColumns={isDisplayTable}
          />
        )}
      </div>
    );
  }
}
export default CatalogTable;
